import React from 'react'
import PropTypes from 'prop-types'

import { FormattedMessage } from '@dragonraider5/react-intl'
import { IS_TRUE } from '../../utils/validations/constants'
import { showFieldError, renderValidation } from '../../utils/validations'
import { getExceptionMessage } from '../../utils/exceptions'
import { LOGIN_PATH } from '../MainRoutes/constants'
import { Link } from 'react-router-dom'
import { SectionWrapper, SectionSubTitle } from '../../components/Section'
import FormRow from '../../components/FormFields/FormRow'
import TextField from '../../components/FormFields/TextField'
import PasswordField from '../../components/FormFields/PasswordField'
import CheckboxField from '../../components/FormFields/CheckboxField'
import FormAction from '../../components/FormFields/FormAction'
import FormError from '../../components/FormFields/FormError'
import FormLinks from '../../components/FormFields/FormLinks'
import PasswordTip from './PasswordTip'
import messages from './messages'

export const renderTermsLink = text => (
  <a
    href='/Condiciones_generales_sistema_de_transmisión_de_solicitudes_de_financiación.pdf'
    title={text}
    target='_blank'
    rel='noopener noreferrer'
  >
    {text}
  </a>
)

export const renderPrivacyLink = text => (
  <a
    href='https://www.triodos.es/es/politica-privacidad'
    title={text}
    target='_blank'
    rel='noopener noreferrer'
  >
    {text}
  </a>
)

function RegisterForm({
  values,
  status = {},
  errors,
  touched,
  isSubmitting,
  handleChange,
  handleBlur,
  handleSubmit
}) {
  return (
    <SectionWrapper>
      <SectionSubTitle>
        <FormattedMessage {...messages.formTitle} />
      </SectionSubTitle>
      <form onSubmit={handleSubmit}>
        <FormRow>
          <TextField
            type='text'
            name='name'
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={showFieldError('name', { errors, touched })}
            help={renderValidation('name', { errors, touched })}
            label={<FormattedMessage {...messages.formFieldName} />}
            full
          />
        </FormRow>
        <FormRow>
          <TextField
            type='text'
            name='lastname'
            value={values.lastname}
            onChange={handleChange}
            onBlur={handleBlur}
            error={showFieldError('lastname', { errors, touched })}
            help={renderValidation('lastname', { errors, touched })}
            label={<FormattedMessage {...messages.formFieldLastname} />}
            full
          />
        </FormRow>
        <FormRow>
          <TextField
            type='text'
            name='nif'
            value={values.nif}
            onChange={handleChange}
            onBlur={handleBlur}
            error={showFieldError('nif', { errors, touched })}
            help={renderValidation('nif', { errors, touched })}
            label={<FormattedMessage {...messages.formFieldNIF} />}
          />
          <TextField
            type='tel'
            name='phone'
            value={values.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            error={showFieldError('phone', { errors, touched })}
            help={renderValidation('phone', { errors, touched })}
            label={<FormattedMessage {...messages.formFieldPhone} />}
          />
        </FormRow>
        <FormRow>
          <TextField
            type='email'
            name='username'
            value={values.username}
            onChange={handleChange}
            onBlur={handleBlur}
            error={showFieldError('username', { errors, touched })}
            help={renderValidation('username', { errors, touched })}
            label={<FormattedMessage {...messages.formFieldUsername} />}
          />
          <PasswordField
            name='password'
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            error={showFieldError('password', { errors, touched })}
            help={renderValidation('password', { errors, touched })}
            label={<FormattedMessage {...messages.formFieldPassword} />}
            tip={<PasswordTip value={values.password} />}
          />
        </FormRow>
        <FormRow leader>
          <CheckboxField
            type='checkbox'
            name='power'
            value={values.power}
            onChange={handleChange}
            onBlur={handleBlur}
            error={showFieldError('power', { errors, touched })}
            help={renderValidation('power', { errors, touched }, { [IS_TRUE]: messages.formValidationsPower })}
            label={<FormattedMessage {...messages.formFieldPower} />}
          />
        </FormRow>
        <FormRow>
          <CheckboxField
            type='checkbox'
            name='terms'
            value={values.terms}
            onChange={handleChange}
            onBlur={handleBlur}
            error={showFieldError('terms', { errors, touched })}
            help={renderValidation('terms', { errors, touched }, { [IS_TRUE]: messages.formValidationsTerms })}
            label={
              <FormattedMessage
                {...messages.formFieldTerms}
                values={{
                  termsLink: (
                    <FormattedMessage {...messages.termsLink}>
                      {renderTermsLink}
                    </FormattedMessage>
                  ),
                  privacyLink: (
                    <FormattedMessage {...messages.privacyLink}>
                      {renderPrivacyLink}
                    </FormattedMessage>
                  )
                }}
              />
            }
          />
        </FormRow>
        {status.error && (
          <FormError>
            <FormattedMessage {...getExceptionMessage(status.error)} />
          </FormError>
        )}
        <FormAction type='submit' disabled={isSubmitting}>
          <FormattedMessage {...messages.formButtonSubmit} />
        </FormAction>
      </form>
      <FormLinks>
        <Link to={LOGIN_PATH}>
          <FormattedMessage {...messages.loginLink} />
        </Link>
      </FormLinks>
    </SectionWrapper>
  )
}

RegisterForm.propTypes = {
  values: PropTypes.shape({
    username: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    lastname: PropTypes.string.isRequired,
    nif: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    terms: PropTypes.bool.isRequired,
    power: PropTypes.bool.isRequired
  }).isRequired,
  errors: PropTypes.shape({
    username: PropTypes.string,
    password: PropTypes.string,
    name: PropTypes.string,
    lastname: PropTypes.string,
    nif: PropTypes.string,
    phone: PropTypes.string,
    terms: PropTypes.string,
    power: PropTypes.string
  }).isRequired,
  touched: PropTypes.shape({
    username: PropTypes.bool,
    password: PropTypes.bool,
    name: PropTypes.bool,
    lastname: PropTypes.bool,
    nif: PropTypes.bool,
    phone: PropTypes.bool,
    terms: PropTypes.bool,
    power: PropTypes.bool
  }).isRequired,
  status: PropTypes.shape({
    error: PropTypes.instanceOf(Error),
    success: PropTypes.object
  }),
  isSubmitting: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired
}

export default RegisterForm
